/**
 * Client Domains Page
 * View and manage domains
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  Plus,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Lock,
  Unlock,
  RefreshCw,
  Settings,
  ExternalLink,
} from 'lucide-react';

interface Domain {
  id: number;
  domainName: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending' | 'transferred';
  registrationDate: string;
  expiryDate: string;
  autoRenew: number;
  locked: number;
  nextDueDate: string;
  recurringAmount: number;
}

const statusConfig = {
  active: {
    label: 'Active',
    icon: CheckCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
    borderColor: 'border-green-700',
  },
  pending: {
    label: 'Pending',
    icon: AlertTriangle,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
    borderColor: 'border-yellow-700',
  },
  expired: {
    label: 'Expired',
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-900/20',
    borderColor: 'border-red-700',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    color: 'text-slate-400',
    bgColor: 'bg-slate-900/20',
    borderColor: 'border-slate-700/50',
  },
  transferred: {
    label: 'Transferred',
    icon: RefreshCw,
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/20',
    borderColor: 'border-purple-700',
  },
};

const DomainsPage: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/client/domains', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setDomains(data.data.domains);
      }
    } catch (error) {
      console.error('Failed to load domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const isExpiringSoon = (domain: Domain) => {
    const daysUntilExpiry = Math.floor(
      (new Date(domain.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const filteredDomains = domains.filter(
    (domain) => filterStatus === 'all' || domain.status === filterStatus
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const days = Math.floor(
      (new Date(expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return days;
  };

  const expiringSoon = domains.filter((d) => isExpiringSoon(d)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Globe className="w-8 h-8" />
            My Domains
          </h1>
          <p className="text-slate-400 mt-2">Manage your domain registrations</p>
        </div>
        <Link
          to="/client/domains/register"
          className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 font-medium"
        >
          <Plus className="w-5 h-5" />
          Register Domain
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Domains</p>
              <p className="text-2xl font-bold text-white mt-1">{domains.length}</p>
            </div>
            <Globe className="w-8 h-8 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {domains.filter((d) => d.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Expiring Soon</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{expiringSoon}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Auto-Renew</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">
                {domains.filter((d) => d.autoRenew).length}
              </p>
            </div>
            <RefreshCw className="w-8 h-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'active', 'pending', 'expired', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === status
                ? 'bg-orange-500 text-white'
                : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Domains List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading domains...</span>
          </div>
        </div>
      ) : filteredDomains.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <Globe className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Domains Found</h3>
          <p className="text-slate-400 mb-6">
            {filterStatus === 'all'
              ? "You don't have any domains yet."
              : `You don't have any ${filterStatus} domains.`}
          </p>
          <Link
            to="/client/domains/register"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Register Your First Domain
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredDomains.map((domain) => {
            const statusInfo = statusConfig[domain.status];
            const StatusIcon = statusInfo.icon;
            const daysUntilExpiry = getDaysUntilExpiry(domain.expiryDate);
            const expiringSoon = isExpiringSoon(domain);

            return (
              <div
                key={domain.id}
                className={`bg-slate-800/80 backdrop-blur-sm rounded-xl border ${statusInfo.borderColor} hover:border-orange-500 transition-colors`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Globe className="w-5 h-5 text-orange-400" />
                          {domain.domainName}
                        </h3>
                        <span
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                        {domain.locked ? (
                          <span className="flex items-center gap-1 px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-xs">
                            <Lock className="w-3 h-3" />
                            Locked
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-1 bg-slate-700/50 text-yellow-400 rounded text-xs">
                            <Unlock className="w-3 h-3" />
                            Unlocked
                          </span>
                        )}
                        {domain.autoRenew ? (
                          <span className="flex items-center gap-1 px-2 py-1 bg-green-900/20 text-green-400 rounded text-xs">
                            <RefreshCw className="w-3 h-3" />
                            Auto-Renew
                          </span>
                        ) : null}
                      </div>
                      {expiringSoon && (
                        <div className="flex items-center gap-2 text-yellow-400 text-sm mb-2">
                          <AlertTriangle className="w-4 h-4" />
                          Expires in {daysUntilExpiry} days - Renew now to avoid losing your
                          domain!
                        </div>
                      )}
                    </div>
                    <Link
                      to={`/client/domains/${domain.id}`}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Manage
                    </Link>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-slate-700/50">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Registered</p>
                      <p className="text-white font-medium flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(domain.registrationDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Expires</p>
                      <p
                        className={`font-medium flex items-center gap-1 ${
                          expiringSoon ? 'text-yellow-400' : 'text-white'
                        }`}
                      >
                        <Calendar className="w-4 h-4" />
                        {formatDate(domain.expiryDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Renewal Price</p>
                      <p className="text-white font-medium">
                        {formatPrice(domain.recurringAmount)}/year
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Next Invoice</p>
                      <p className="text-white font-medium">{formatDate(domain.nextDueDate)}</p>
                    </div>
                  </div>

                  {domain.status === 'active' && (
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <div className="flex flex-wrap gap-2">
                        <button className="px-3 py-1.5 bg-slate-700/50 text-white rounded text-sm hover:bg-slate-600/50 transition-colors">
                          DNS Management
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700/50 text-white rounded text-sm hover:bg-slate-600/50 transition-colors">
                          Email Forwarding
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700/50 text-white rounded text-sm hover:bg-slate-600/50 transition-colors">
                          WHOIS
                        </button>
                        {!domain.autoRenew && (
                          <button className="px-3 py-1.5 bg-green-700 text-white rounded text-sm hover:bg-green-600 transition-colors flex items-center gap-1">
                            <RefreshCw className="w-4 h-4" />
                            Enable Auto-Renew
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DomainsPage;
